import type { TFunction } from 'i18next';
import { ApiError } from '@/api/client';

/**
 * Traduce un errore tecnico in un messaggio per l'utente.
 * ---------------------------------------------------------------------------
 * Punto UNICO in cui si decide cosa leggere quando qualcosa va storto. Senza,
 * ogni pagina finisce per stampare `error.message`, cioè un testo pensato per
 * chi sviluppa.
 *
 * La ramificazione è sul **codice applicativo**, non sul messaggio: il
 * messaggio è una frase in italiano che il backend può riformulare quando
 * vuole, il codice no.
 *
 * Tre casi meritano un testo proprio invece di "si è verificato un errore":
 *
 *  · **429 `throttled`** — dichiarato su tutti e 33 gli endpoint (200
 *    richieste/minuto sugli endpoint pubblici). È l'errore che il sito
 *    pubblico incontrerà più spesso, e **non è un guasto**: dire "errore" a
 *    chi ha solo ricaricato troppo in fretta è sbagliato e allarmante.
 *  · **503 `email_non_configurata`** — la decisione 24 rinvia la
 *    configurazione email e il backend rende l'assenza esplicita invece di
 *    fingere l'invio. Nel back office si vedrà davvero finché non si
 *    configura: il messaggio deve dire **cosa manca**, così chi lo legge sa
 *    che è una configurazione mancante e non un bug da segnalare.
 *  · **rete irraggiungibile** — è un problema del dispositivo o della
 *    connessione, non del portale.
 */

export interface MessaggioErrore {
  readonly titolo: string;
  readonly testo: string;
  /** `true` se ha senso riprovare la stessa azione fra poco. */
  readonly riprovabile: boolean;
  /** Dettaglio tecnico, da mostrare in secondo piano. Mai al posto del testo. */
  readonly dettaglioTecnico: string | null;
}

export function messaggioErrore(errore: unknown, t: TFunction): MessaggioErrore {
  if (errore instanceof ApiError) {
    if (errore.isNetwork) {
      return {
        titolo: t('error_network_title', 'Connessione non disponibile'),
        testo: t(
          'error_network_body',
          'Non è stato possibile contattare il portale. Controlla la connessione e riprova.',
        ),
        riprovabile: true,
        dettaglioTecnico: null,
      };
    }

    if (errore.isThrottled) {
      return {
        titolo: t('error_throttled_title', 'Troppe richieste'),
        // Il messaggio del backend contiene i secondi di attesa ("Riprovare
        // fra 41 secondi"): è utile e va mostrato.
        testo:
          errore.message ||
          t(
            'error_throttled_body',
            'Hai fatto troppe richieste in poco tempo. Attendi qualche istante e riprova.',
          ),
        riprovabile: true,
        dettaglioTecnico: null,
      };
    }

    if (errore.isEmailNonConfigurata) {
      return {
        titolo: t('error_email_title', 'Invio email non configurato'),
        testo: t(
          'error_email_body',
          "Il servizio di posta non è ancora configurato su questo ambiente, quindi l'email non è stata inviata. Non è un guasto: la configurazione è rinviata. L'operazione va completata per altra via.",
        ),
        riprovabile: false,
        dettaglioTecnico: errore.message,
      };
    }

    if (errore.isUnauthorized) {
      return {
        titolo: t('error_unauthorized_title', 'Sessione scaduta'),
        testo: t('error_unauthorized_body', 'Accedi di nuovo per continuare.'),
        riprovabile: false,
        dettaglioTecnico: null,
      };
    }

    if (errore.isForbidden) {
      return {
        titolo: t('error_forbidden_title', 'Operazione non consentita'),
        testo:
          errore.message ||
          t('error_forbidden_body', 'Il tuo ruolo non permette questa operazione.'),
        riprovabile: false,
        dettaglioTecnico: null,
      };
    }

    if (errore.isValidation) {
      return {
        titolo: t('error_validation_title', 'Dati non validi'),
        testo:
          errore.message || t('error_validation_body', 'Controlla i campi segnalati.'),
        riprovabile: false,
        dettaglioTecnico: null,
      };
    }

    if (errore.isConflict) {
      return {
        titolo: t('error_conflict_title', 'Operazione non ammessa'),
        testo: errore.message,
        riprovabile: false,
        dettaglioTecnico: null,
      };
    }

    // Errore con busta valida ma codice non previsto: si mostra il messaggio
    // del backend, che è comunque scritto per una persona.
    if (errore.codice) {
      return {
        titolo: t('error_generic_title', 'Si è verificato un errore'),
        testo: errore.message,
        riprovabile: true,
        dettaglioTecnico: `${errore.status} · ${errore.codice}`,
      };
    }

    // Risposta fuori contratto: nessun codice da mostrare.
    return {
      titolo: t('error_generic_title', 'Si è verificato un errore'),
      testo: t(
        'error_generic_body',
        'Il portale non ha potuto completare la richiesta. Riprova fra poco.',
      ),
      riprovabile: true,
      dettaglioTecnico: String(errore.status),
    };
  }

  return {
    titolo: t('error_generic_title', 'Si è verificato un errore'),
    testo: t(
      'error_generic_body',
      'Il portale non ha potuto completare la richiesta. Riprova fra poco.',
    ),
    riprovabile: true,
    dettaglioTecnico: errore instanceof Error ? errore.message : null,
  };
}
